import React, { createContext, useContext, useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";

// Safe hook that won't crash if LanguageProvider hasn't mounted yet (HMR race)
const useSafeLanguage = () => {
  try {
    return useLanguage();
  } catch {
    return { language: 'en' as const, isRTL: false, t: (k: string) => k, changeLanguage: () => {} };
  }
};

interface RTLContextType {
  isRTL: boolean;
  direction: "ltr" | "rtl";
  toggleDirection: () => void;
  formatNumber: (num: number) => string;
  formatCurrency: (amount: number) => string;
  formatDate: (date: Date) => string;
}

const RTLContext = createContext<RTLContextType | null>(null);

export const useRTL = () => {
  const context = useContext(RTLContext);
  if (!context) {
    throw new Error('useRTL must be used within an RTLProvider');
  }
  return context;
};

interface RTLProviderProps {
  children: React.ReactNode;
}

export const RTLProvider: React.FC<RTLProviderProps> = ({ children }) => {
  const { language } = useSafeLanguage();
  const [direction, setDirection] = useState<"ltr" | "rtl">("ltr");

  useEffect(() => {
    // RTL languages: Urdu, Sindhi, Saraiki, Balochi, Pashto, Arabic
    // This must match the RTL list in LanguageContext.tsx
    const rtlLanguages = ['ur', 'sd', 'skr', 'bal', 'ps', 'ar'];
    const isRTL = rtlLanguages.includes(language);
    
    setDirection(isRTL ? 'rtl' : 'ltr');
    
    // Update document direction
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
    
    // Update body class for RTL-specific styles
    document.body.classList.toggle('rtl', isRTL);
    
  }, [language]);

  const toggleDirection = () => {
    const newDirection = direction === 'ltr' ? 'rtl' : 'ltr';
    setDirection(newDirection);
    document.documentElement.dir = newDirection;
    document.body.classList.toggle('rtl', newDirection === 'rtl');
  };

  // Pakistan-specific number formatting
  // Pakistan-specific number formatting
  const rtlLanguages = ['ur', 'sd', 'skr', 'bal', 'ps', 'ar'];
  const isRTLLanguage = rtlLanguages.includes(language);

  const formatNumber = (num: number): string => {
    if (isRTLLanguage) {
      return new Intl.NumberFormat('ur-PK').format(num);
    }
    return new Intl.NumberFormat('en-PK').format(num);
  };

  // Pakistani Rupee currency formatting
  const formatCurrency = (amount: number): string => {
    const formatter = new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    
    let formatted = formatter.format(amount);
    
    // Replace "PKR" with "Rs" for shorter display
    formatted = formatted.replace('PKR', 'Rs');
    
    // For RTL languages, adjust the currency symbol position
    if (direction === 'rtl') {
      formatted = formatted.replace('Rs ', '') + ' روپے';
    }
    
    return formatted;
  };

  // Pakistan-specific date formatting
  const formatDate = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };

    if (isRTLLanguage) {
      return new Intl.DateTimeFormat('ur-PK', options).format(date);
    }
    
    // English with Pakistani locale
    return new Intl.DateTimeFormat('en-PK', options).format(date);
  };

  const contextValue: RTLContextType = {
    isRTL: direction === 'rtl',
    direction,
    toggleDirection,
    formatNumber,
    formatCurrency,
    formatDate
  };

  return (
    <RTLContext.Provider value={contextValue}>
      <div dir={direction} className={`rtl-container ${direction}`}>
        {children}
      </div>
    </RTLContext.Provider>
  );
};

// RTL-aware component wrapper
export const RTLWrapper: React.FC<{ 
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = "" }) => {
  const { isRTL } = useRTL();
  
  return (
    <div className={`${className} ${isRTL ? 'rtl-layout' : 'ltr-layout'}`}>
      {children}
    </div>
  );
};

// RTL-aware flex container
export const RTLFlex: React.FC<{
  children: React.ReactNode;
  direction?: 'row' | 'column';
  className?: string;
}> = ({ children, direction = 'row', className = "" }) => {
  const { isRTL } = useRTL();
  
  const flexDirection = isRTL && direction === 'row' ? 'row-reverse' : direction;
  
  return (
    <div className={`flex flex-${flexDirection} ${className}`}>
      {children}
    </div>
  );
};

// RTL-aware text alignment
export const RTLText: React.FC<{
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
  className?: string;
}> = ({ children, align, className = "" }) => {
  const { isRTL } = useRTL();
  
  let textAlign = align;
  if (!textAlign) {
    textAlign = isRTL ? 'right' : 'left';
  } else if (textAlign === 'left' && isRTL) {
    textAlign = 'right';
  } else if (textAlign === 'right' && isRTL) {
    textAlign = 'left';
  }
  
  return (
    <div className={`text-${textAlign} ${className}`}>
      {children}
    </div>
  );
};